/** The machine from the setup prototype (site/site/demo/app.html): a case, a glass screen, one line per check. */
import { useEffect, useState } from 'react'
import { addressOf } from '../../domain/copy'
import { lineOk, stripHeadline, type MachineLine } from '../../domain/machine'
import { Check, ChevronDown, Mark, Out } from '../components/icons'
import { useFounder } from './context'

function lineSide(l: MachineLine): string {
  if (l.status === 'running') return 'now'
  if (l.status === 'failing') return 'slower'
  return ''
}

export function MachineScreen({ waitsFor }: { waitsFor?: { key: string; step: string } | null }) {
  const { lines, progress, state } = useFounder()
  return (
    <div className="m-case">
      <div className="m-bezel">
        <div className="m-screen">
          <div className="m-top">
            <b>{progress.ready ? 'Your computer is ready' : 'We set up your computer'}</b>
            <span className="g-mono">
              {progress.done} of {progress.total}
            </span>
          </div>
          <ol className="m-lines" aria-live="polite">
            {lines.map((l) => (
              <li key={l.key} className={`m-${l.status}`}>
                <span className="m-tk">{l.status === 'ok' && <Check size={11} />}</span>
                <span>{l.label}</span>
                {waitsFor?.key === l.key && l.status !== 'ok' ? <span className="m-need">{waitsFor.step} waits</span> : <span className="g-mono">{lineSide(l)}</span>}
              </li>
            ))}
          </ol>
          <div className="m-foot">
            <span className="g-mono">{state.world.founder.name}</span>
            <span className="g-mono">{state.world.founder.subdomain ? `${state.world.founder.subdomain}.cpg.computer` : ''}</span>
          </div>
        </div>
      </div>
      <div className="m-chin">
        <span className="m-led">
          <i aria-hidden="true" />
          <Mark size={16} />
        </span>
        <span className="m-slot" aria-hidden="true" />
      </div>
    </div>
  )
}

const wide = () => typeof matchMedia !== 'undefined' && matchMedia('(min-width: 1081px)').matches

/**
 * The background strip, the way a new iPhone keeps setting up: headline, the current line, tap to open the machine.
 * Says "Your computer is ready" when the last check lands, then folds away. Not shown at all if it was ready already.
 */
export function SetupStrip({ waitsFor }: { waitsFor?: { key: string; step: string } | null }) {
  const { progress } = useFounder()
  const [open, setOpen] = useState(wide)
  const [wasReady] = useState(progress.ready)
  const [folded, setFolded] = useState(progress.ready)
  useEffect(() => {
    if (!progress.ready || wasReady) return
    const t = setTimeout(() => setFolded(true), 6000)
    return () => clearTimeout(t)
  }, [progress.ready, wasReady])
  if (folded) return null

  const cur = progress.current
  const sub = progress.ready
    ? 'All checks passed'
    : !progress.started
      ? 'Nothing running yet'
      : progress.failing
        ? 'A person is looking at it'
        : cur
          ? cur.label
          : ''
  return (
    <div className={`strip-wrap${progress.ready ? ' is-ready' : ''}`}>
      <div className="strip-bar">
      <button className="strip" type="button" aria-expanded={open} aria-controls="machine" onClick={() => setOpen((o) => !o)}>
        <span className={`s-dot ${progress.ready ? 'ok' : progress.started ? 'run' : 'idle'}`}>{progress.ready && <Check size={11} />}</span>
        <span className="s-text">
          <span className="s-head">{stripHeadline(progress)}</span>
          <span className="s-sub">{sub}</span>
        </span>
        <ChevronDown />
      </button>
      {progress.started && !progress.ready && (
        <div className="s-meter" aria-hidden="true">
          <i style={{ width: `${Math.round((progress.done / Math.max(1, progress.total)) * 100)}%` }} />
        </div>
      )}
      </div>
      <div id="machine" hidden={!open} className="s-machine">
        <MachineScreen waitsFor={waitsFor} />
        <p className="label m-cap">Each line is a check that ran on your computer.</p>
      </div>
    </div>
  )
}

/** "Your address is live · Visit", as soon as the address check is ok. */
export function AddressCard() {
  const { lines, state } = useFounder()
  const host = addressOf(state.world)
  if (!host || !lineOk(lines, 'address')) return null
  return (
    <div className="addr-card" role="status">
      <span className="tick ok">
        <Check />
      </span>
      <span className="addr-main">
        <b>Your address is live</b>
        <span className="mono">{host}</span>
      </span>
      <a className="btn btn-ink btn-sm" href={`https://${host}`} target="_blank" rel="noreferrer">
        Visit <Out />
      </a>
    </div>
  )
}

/** The private preview address, once the computer serves one. */
export function PreviewNote({ empty }: { empty?: string }) {
  const { state } = useFounder()
  const url = state.world.preview?.url
  if (!url) return empty ? <p className="aside">{empty}</p> : null
  const host = url.replace(/^https?:\/\//, '').replace(/\/+$/, '')
  return (
    <div className="preview-note">
      <a className="url-chip" href={url} target="_blank" rel="noreferrer">
        {host} <Out />
      </a>
      <p className="aside">
        Your app with your agent's latest changes, before you ship. It's always on and updates as your agent works. Only
        people you've added can open it.
      </p>
    </div>
  )
}
